import { useEffect, useRef, useState } from 'react'
import { Box, Grid, Line } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { hasInterference } from '../utils/collisionDetection'
import { snapPosition } from '../utils/snapUtils'
import ContextMenu from './ContextMenu'
import FurnitureHandles from './FurnitureHandles'
import ChairMesh from './ChairMesh'
import BedMesh from './BedMesh'
import TVStandMesh from './TVStandMesh'
import BookshelfMesh from './BookshelfMesh'
// 新しいメッシュ
import PCDeskMesh from './PCDeskMesh'
import LowTableMesh from './LowTableMesh'
import GamingChairMesh from './GamingChairMesh'
import SofaMesh from './SofaMesh'
import FloorChairMesh from './FloorChairMesh'
import MattressMesh from './MattressMesh'
import LoftBedMesh from './LoftBedMesh'
import StorageShelfMesh from './StorageShelfMesh'
import TVStandNewMesh from './TVStandNewMesh'
import HangerRackMesh from './HangerRackMesh'
import RefrigeratorMesh from './RefrigeratorMesh'
import WashingMachineMesh from './WashingMachineMesh'
import FullMirrorMesh from './FullMirrorMesh'
import PlantMesh from './PlantMesh'

export default function Room3D({ room, furnitureList, collisionResult, selectedId, ignoredCollisions, sceneRef, furnitureGroupRef, onSelect, isMobile, controlsRef, onUpdatePosition, onUpdatePositionBatch, onUpdateSize, onUpdateSizeAndPosition, onUpdateRotation, onUpdateSubtype, onDelete, onDuplicate, onIgnoreCollision, onUnignoreCollision, isSnapEnabled = true, openContextMenuId = null, onContextMenuChange }) {
    // cmをThree.jsの単位に変換（1cm = 0.01単位）
    const scale = 0.01
    const { scene, camera, gl } = useThree()
    const localFurnitureGroupRef = useRef()

    // ホバー・ドラッグ状態
    const [hoveredId, setHoveredId] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [draggedId, setDraggedId] = useState(null)
    const [hasMoved, setHasMoved] = useState(false) // ドラッグ中に実際に移動したか
    const floorPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0))
    const raycaster = useRef(new THREE.Raycaster())
    const mouse = useRef(new THREE.Vector2())
    const dragStartPos = useRef({ x: 0, y: 0 }) // ドラッグ開始位置
    const isFirstMoveRef = useRef(false) // ドラッグ開始後の最初の移動かどうか
    const DRAG_THRESHOLD = 5 // ドラッグ判定の閾値（ピクセル）

    // コンテキストメニュー状態
    const [contextMenuFurnitureId, setContextMenuFurnitureId] = useState(null)
    const longPressTimerRef = useRef(null)
    const LONG_PRESS_DURATION = 500 // 長押し判定時間（ms）

    // 外部からのコンテキストメニュー制御
    useEffect(() => {
        if (openContextMenuId !== null && openContextMenuId !== contextMenuFurnitureId) {
            setContextMenuFurnitureId(openContextMenuId)
        }
    }, [openContextMenuId])

    // コンテキストメニュー変更時に親に通知
    const handleSetContextMenuFurnitureId = (id) => {
        setContextMenuFurnitureId(id)
        onContextMenuChange?.(id)
    }

    // ハンドル操作状態（refは同期的なチェック用、stateはUI更新用）
    const [isHandleDragging, setIsHandleDragging] = useState(false)
    const isHandleDraggingRef = useRef(false)

    // スナップライン表示用
    const [snapLines, setSnapLines] = useState([])
    const snapLineTimeoutRef = useRef(null)

    // sceneRefにシーンを設定
    useEffect(() => {
        if (sceneRef) {
            sceneRef.current = scene
        }
    }, [scene, sceneRef])

    // 家具グループのrefを外部に渡す
    useEffect(() => {
        if (furnitureGroupRef && localFurnitureGroupRef.current) {
            furnitureGroupRef.current = localFurnitureGroupRef.current
        }
    }, [furnitureGroupRef, furnitureList])

    // ドラッグ中のマウス移動処理
    useEffect(() => {
        if (!isDragging || !draggedId || !onUpdatePosition) return

        const handlePointerMove = (e) => {
            // ハンドル操作中は家具位置の更新をスキップ
            if (isHandleDraggingRef.current) {
                return
            }

            // 移動量が閾値を超えているか確認
            const dx = e.clientX - dragStartPos.current.x
            const dy = e.clientY - dragStartPos.current.y
            const distance = Math.sqrt(dx * dx + dy * dy)

            if (distance < DRAG_THRESHOLD) {
                return // 閾値未満なら移動処理をスキップ
            }

            // 実際に移動したフラグを立てる
            setHasMoved(true)

            const rect = gl.domElement.getBoundingClientRect()
            mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
            mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1

            raycaster.current.setFromCamera(mouse.current, camera)
            const intersectPoint = new THREE.Vector3()
            raycaster.current.ray.intersectPlane(floorPlane.current, intersectPoint)

            if (intersectPoint) {
                // Three.js座標をcm単位に変換
                let newX = Math.round(intersectPoint.x / scale)
                let newZ = Math.round(intersectPoint.z / scale)

                // 壁境界内に制限（家具サイズと回転を考慮）
                const furniture = furnitureList.find(f => f.id === draggedId)
                if (furniture) {
                    const rotation = furniture.rotation || 0
                    const isRotated90 = Math.abs(rotation % 180) === 90
                    const effectiveWidth = isRotated90 ? furniture.size.depth : furniture.size.width
                    const effectiveDepth = isRotated90 ? furniture.size.width : furniture.size.depth

                    const halfWidth = effectiveWidth / 2
                    const halfDepth = effectiveDepth / 2
                    const roomHalfWidth = room.width / 2
                    const roomHalfDepth = room.depth / 2

                    // 基本的な境界制限
                    newX = Math.max(-roomHalfWidth + halfWidth, Math.min(roomHalfWidth - halfWidth, newX))
                    newZ = Math.max(-roomHalfDepth + halfDepth, Math.min(roomHalfDepth - halfDepth, newZ))

                    // スナップ処理（有効な場合）
                    if (isSnapEnabled) {
                        const otherFurniture = furnitureList.filter(f => f.id !== draggedId)
                        const snapResult = snapPosition(
                            { x: newX, z: newZ },
                            furniture.size,
                            rotation,
                            room,
                            otherFurniture
                        )
                        newX = snapResult.x
                        newZ = snapResult.z

                        // スナップライン表示
                        if (snapResult.snapLines.length > 0) {
                            setSnapLines(snapResult.snapLines)
                            // 既存のタイムアウトをクリア
                            if (snapLineTimeoutRef.current) {
                                clearTimeout(snapLineTimeoutRef.current)
                            }
                            // 0.5秒後にスナップラインを消す
                            snapLineTimeoutRef.current = setTimeout(() => {
                                setSnapLines([])
                            }, 500)
                        }
                    }
                }

                // 一括更新で両軸を同時に更新
                // 初回のみ履歴保存し、2回目以降は上書き
                const overwrite = !isFirstMoveRef.current
                if (onUpdatePositionBatch) {
                    onUpdatePositionBatch(draggedId, newX, newZ, overwrite)
                } else {
                    onUpdatePosition(draggedId, 'x', newX, overwrite)
                    onUpdatePosition(draggedId, 'z', newZ, overwrite)
                }
                // 初回移動完了
                isFirstMoveRef.current = false
            }
        }

        const handlePointerUp = () => {
            setIsDragging(false)
            setDraggedId(null)
            setHasMoved(false)
            setSnapLines([]) // スナップラインをクリア
            if (controlsRef?.current) {
                controlsRef.current.enabled = true
            }
            document.body.style.cursor = 'auto'
        }

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)

        return () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
        }
    }, [isDragging, draggedId, camera, gl, onUpdatePosition, controlsRef, scale, furnitureList, room, isSnapEnabled])

    // カーソル変更
    useEffect(() => {
        if (isDragging) {
            document.body.style.cursor = 'grabbing'
        } else if (hoveredId) {
            document.body.style.cursor = 'pointer'
        } else {
            document.body.style.cursor = 'auto'
        }
    }, [hoveredId, isDragging])

    return (
        <>
            {/* 照明 */}
            <ambientLight intensity={0.6} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <pointLight position={[-10, -10, -5]} intensity={0.5} />
            <spotLight position={[0, 10, 0]} intensity={0.5} angle={0.3} penumbra={1} />

            {/* グリッド（床） */}
            <Grid
                args={[room.width * scale, room.depth * scale]}
                cellSize={0.5}
                cellThickness={0.5}
                cellColor="#6b7280"
                sectionSize={2.5}
                sectionThickness={1}
                sectionColor="#0ea5e9"
                fadeDistance={25}
                fadeStrength={1}
                position={[0, 0, 0]}
            />

            {/* 床クリック用の透明メッシュ（選択解除用） */}
            <mesh
                position={[0, -0.01, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                onPointerDown={(e) => {
                    e.stopPropagation()
                    if (onSelect) {
                        onSelect(null)
                    }
                    handleSetContextMenuFurnitureId(null)
                }}
            >
                <planeGeometry args={[room.width * scale * 2, room.depth * scale * 2]} />
                <meshBasicMaterial transparent opacity={0} />
            </mesh>

            {/* 部屋の境界（ワイヤーフレーム） */}
            <Box
                args={[room.width * scale, room.height * scale, room.depth * scale]}
                position={[0, (room.height * scale) / 2, 0]}
            >
                <meshBasicMaterial
                    color="#ffffff"
                    wireframe
                    opacity={0.2}
                    transparent
                />
            </Box>

            {/* スナップライン表示 */}
            {snapLines.map((line, index) => {
                const roomHalfWidth = room.width * scale / 2
                const roomHalfDepth = room.depth * scale / 2
                const lineHeight = room.height * scale * 0.8

                if (line.axis === 'x') {
                    const xPos = line.position * scale
                    return (
                        <Line
                            key={`snap-${index}`}
                            points={[
                                [xPos, 0.01, -roomHalfDepth],
                                [xPos, 0.01, roomHalfDepth]
                            ]}
                            color="#00ffff"
                            lineWidth={2}
                            opacity={0.8}
                            transparent
                        />
                    )
                } else if (line.axis === 'z') {
                    const zPos = line.position * scale
                    return (
                        <Line
                            key={`snap-${index}`}
                            points={[
                                [-roomHalfWidth, 0.01, zPos],
                                [roomHalfWidth, 0.01, zPos]
                            ]}
                            color="#00ffff"
                            lineWidth={2}
                            opacity={0.8}
                            transparent
                        />
                    )
                }
                return null
            })}

            {/* 家具の表示 */}
            <group ref={localFurnitureGroupRef} name="furniture-group">
                {furnitureList.map((furniture) => {
                    const width = furniture.size.width * scale
                    const height = furniture.size.height * scale
                    const depth = furniture.size.depth * scale
                    const x = furniture.position.x * scale
                    // position.yは床からの高さ（通常0）、家具の中心はheight/2上
                    const y = height / 2
                    const z = furniture.position.z * scale

                    // 干渉チェック
                    const hasIssue = hasInterference(furniture.id, collisionResult)
                    const isSelected = furniture.id === selectedId

                    // 無視された干渉かどうかをチェック
                    let isIgnoredCollision = false
                    if (hasIssue && collisionResult.collisions.has(furniture.id)) {
                        const collidingWith = collisionResult.collisions.get(furniture.id)
                        // すべての干渉が無視されている場合のみ、無視された干渉とみなす
                        isIgnoredCollision = Array.from(collidingWith).every(otherId => {
                            const key = [furniture.id, otherId].sort().join(':')
                            return ignoredCollisions.has(key)
                        }) && !collisionResult.outOfBounds.has(furniture.id)
                    }

                    // 色を決定: 無視された干渉=黄色、干渉あり=赤、正常=緑
                    const color = isIgnoredCollision ? '#eab308' : hasIssue ? '#ef4444' : '#10b981'

                    const isHovered = furniture.id === hoveredId

                    // 共通のprops（相対位置を渡す）
                    const meshProps = {
                        width,
                        height,
                        depth,
                        size: { width: furniture.size.width, height: furniture.size.height, depth: furniture.size.depth },
                        position: [0, y, 0], // groupの位置を基準とした相対位置
                        color,
                        isSelected,
                        isHovered
                    }

                    // 家具タイプに応じた表示
                    const renderFurniture = () => {
                        // === desk カテゴリ ===
                        if (furniture.category === 'desk' && furniture.subtype === 'pc') {
                            return <PCDeskMesh {...meshProps} />
                        }
                        if (furniture.category === 'desk' && furniture.subtype === 'lowTable') {
                            return <LowTableMesh {...meshProps} />
                        }

                        // === chair カテゴリ ===
                        if (furniture.category === 'chair' && furniture.subtype === 'desk') {
                            return <ChairMesh {...meshProps} />
                        }
                        if (furniture.category === 'chair' && furniture.subtype === 'gaming') {
                            return <GamingChairMesh {...meshProps} />
                        }
                        if (furniture.category === 'chair' && furniture.subtype === 'sofa') {
                            return <SofaMesh {...meshProps} />
                        }
                        if (furniture.category === 'chair' && furniture.subtype === 'floor') {
                            return <FloorChairMesh {...meshProps} />
                        }

                        // === bed カテゴリ ===
                        if (furniture.category === 'bed' && furniture.subtype === 'standard') {
                            return <BedMesh {...meshProps} />
                        }
                        if (furniture.category === 'bed' && furniture.subtype === 'mattress') {
                            return <MattressMesh {...meshProps} />
                        }
                        if (furniture.category === 'bed' && furniture.subtype === 'loft') {
                            return <LoftBedMesh {...meshProps} />
                        }

                        // === shelf カテゴリ ===
                        if (furniture.category === 'shelf' && furniture.subtype === 'bookshelf') {
                            return <BookshelfMesh {...meshProps} />
                        }
                        if (furniture.category === 'shelf' && furniture.subtype === 'storage') {
                            return <StorageShelfMesh {...meshProps} />
                        }
                        if (furniture.category === 'shelf' && furniture.subtype === 'tvStand') {
                            return <TVStandNewMesh {...meshProps} />
                        }
                        if (furniture.category === 'shelf' && furniture.subtype === 'hanger') {
                            return <HangerRackMesh {...meshProps} />
                        }

                        // === appliances カテゴリ ===
                        if (furniture.category === 'appliances' && furniture.subtype === 'refrigerator') {
                            return <RefrigeratorMesh {...meshProps} />
                        }
                        if (furniture.category === 'appliances' && furniture.subtype === 'washer') {
                            return <WashingMachineMesh {...meshProps} />
                        }

                        // === others カテゴリ ===
                        if (furniture.category === 'others' && furniture.subtype === 'mirror') {
                            return <FullMirrorMesh {...meshProps} />
                        }
                        if (furniture.category === 'others' && furniture.subtype === 'plant') {
                            return <PlantMesh {...meshProps} />
                        }

                        // テレビ台（旧storage用、後方互換）
                        if (furniture.category === 'storage' && furniture.subtype === 'tv') {
                            return <TVStandMesh {...meshProps} />
                        }

                        // デフォルト: 単純な箱
                        return (
                            <>
                                {/* メインの箱 */}
                                <Box args={[width, height, depth]} position={[0, y, 0]}>
                                    <meshStandardMaterial
                                        color={color}
                                        metalness={0.3}
                                        roughness={0.4}
                                        opacity={isSelected ? 1 : 0.9}
                                        transparent
                                    />
                                </Box>

                                {/* ワイヤーフレーム */}
                                <Box args={[width, height, depth]} position={[0, y, 0]}>
                                    <meshBasicMaterial
                                        color={hasIssue ? '#ef4444' : '#0ea5e9'}
                                        wireframe
                                        opacity={isSelected ? 0.6 : 0.3}
                                        transparent
                                    />
                                </Box>

                                {/* 選択時のハイライト */}
                                {isSelected && (
                                    <Box args={[width, height, depth]} position={[0, y, 0]}>
                                        <meshBasicMaterial
                                            color="#fbbf24"
                                            wireframe
                                            opacity={0.8}
                                            transparent
                                        />
                                    </Box>
                                )}

                                {/* ホバー時のハイライト */}
                                {isHovered && !isSelected && (
                                    <Box args={[width * 1.02, height * 1.02, depth * 1.02]} position={[0, y, 0]}>
                                        <meshBasicMaterial
                                            color="#60a5fa"
                                            wireframe
                                            opacity={0.6}
                                            transparent
                                        />
                                    </Box>
                                )}
                            </>
                        )
                    }

                    return (
                        <group
                            key={furniture.id}
                            position={[x, 0, z]}
                            rotation={[0, (furniture.rotation || 0) * Math.PI / 180, 0]}
                            onPointerEnter={(e) => {
                                e.stopPropagation()
                                setHoveredId(furniture.id)
                            }}
                            onPointerLeave={(e) => {
                                e.stopPropagation()
                                setHoveredId(null)
                                // 長押しタイマーをクリア
                                if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current)
                                    longPressTimerRef.current = null
                                }
                            }}
                            onContextMenu={(e) => {
                                // 右クリックメニュー（デスクトップ）
                                e.stopPropagation()
                                e.nativeEvent.preventDefault()
                                handleSetContextMenuFurnitureId(furniture.id)
                                if (onSelect) {
                                    onSelect(furniture.id)
                                }
                            }}
                            onPointerDown={(e) => {
                                e.stopPropagation()

                                // クリック時点で即座に選択
                                if (onSelect && (e.pointerType === 'touch' || e.pointerType === 'mouse')) {
                                    onSelect(furniture.id)
                                }

                                // 長押し検出（タッチ用）
                                if (e.pointerType === 'touch') {
                                    longPressTimerRef.current = setTimeout(() => {
                                        handleSetContextMenuFurnitureId(furniture.id)
                                        longPressTimerRef.current = null
                                    }, LONG_PRESS_DURATION)
                                }

                                // マウスまたはタッチでドラッグ準備（ハンドル操作中は無効）
                                if (!isHandleDraggingRef.current && ((e.pointerType === 'mouse' && e.button === 0) || e.pointerType === 'touch')) {
                                    if (onUpdatePosition || onUpdatePositionBatch) {
                                        // ドラッグ開始位置を記録
                                        dragStartPos.current = { x: e.clientX, y: e.clientY }
                                        isFirstMoveRef.current = true // ドラッグ開始フラグ
                                        setHasMoved(false)
                                        setIsDragging(true)
                                        setDraggedId(furniture.id)
                                        if (controlsRef?.current) {
                                            controlsRef.current.enabled = false
                                        }
                                    }
                                }
                            }}
                            onPointerUp={(e) => {
                                // ドラッグ終了処理（選択はonPointerDownで処理済み）
                                e.stopPropagation()
                                // 長押しタイマーをクリア
                                if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current)
                                    longPressTimerRef.current = null
                                }
                            }}
                            onPointerMove={(e) => {
                                // タッチ中に動いたら長押しキャンセル
                                if (e.pointerType === 'touch' && longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current)
                                    longPressTimerRef.current = null
                                }
                            }}
                        >
                            {renderFurniture()}

                            {/* 右クリックメニュー表示時のみハンドル表示（モバイル以外） */}
                            {!isMobile && contextMenuFurnitureId === furniture.id && onUpdateSize && onUpdateRotation && (
                                <FurnitureHandles
                                    furniture={{
                                        ...furniture,
                                        width,
                                        height,
                                        depth,
                                        size: furniture.size,
                                    }}
                                    onRotate={(angle, overwrite) => onUpdateRotation(furniture.id, angle, overwrite)}
                                    onResizeWidth={(newWidth, overwrite) => onUpdateSize(furniture.id, 'width', newWidth, overwrite)}
                                    onResizeDepth={(newDepth, overwrite) => onUpdateSize(furniture.id, 'depth', newDepth, overwrite)}
                                    onResizeHeight={(newHeight, overwrite) => onUpdateSize(furniture.id, 'height', newHeight, overwrite)}
                                    onResizeWithOffset={(sizeDim, newSize, posAxis, offset, initialPosX, initialPosZ, overwrite) => {
                                        // 回転を考慮した位置オフセットの適用（初期位置ベース）
                                        const rotation = furniture.rotation || 0
                                        const rad = rotation * Math.PI / 180
                                        let offsetX = 0, offsetZ = 0
                                        if (posAxis === 'x') {
                                            offsetX = offset * Math.cos(rad)
                                            offsetZ = offset * Math.sin(rad)
                                        } else if (posAxis === 'z') {
                                            offsetX = -offset * Math.sin(rad)
                                            offsetZ = offset * Math.cos(rad)
                                        }
                                        // 初期位置 + オフセットで絶対位置を計算
                                        const newPosX = initialPosX + offsetX
                                        const newPosZ = initialPosZ + offsetZ
                                        // サイズと位置を同時に更新（1回のsetStateで両方更新）
                                        if (onUpdateSizeAndPosition) {
                                            onUpdateSizeAndPosition(furniture.id, sizeDim, newSize, newPosX, newPosZ, overwrite)
                                        } else {
                                            // フォールバック: 従来の個別更新
                                            onUpdateSize(furniture.id, sizeDim, newSize, overwrite)
                                            if (onUpdatePositionBatch) {
                                                onUpdatePositionBatch(furniture.id, newPosX, newPosZ, overwrite)
                                            }
                                        }
                                    }}
                                    onDragStart={() => {
                                        isHandleDraggingRef.current = true
                                        setIsHandleDragging(true)
                                        if (controlsRef?.current) {
                                            controlsRef.current.enabled = false
                                        }
                                    }}
                                    onDragEnd={() => {
                                        isHandleDraggingRef.current = false
                                        setIsHandleDragging(false)
                                        if (controlsRef?.current) {
                                            controlsRef.current.enabled = true
                                        }
                                    }}
                                />
                            )}

                            {/* コンテキストメニュー（モバイル以外） */}
                            {!isMobile && contextMenuFurnitureId === furniture.id && onUpdateSubtype && onDelete && (
                                <ContextMenu
                                    furniture={furniture}
                                    position3D={[0, y + 0.5, 0]}
                                    onClose={() => handleSetContextMenuFurnitureId(null)}
                                    onUpdateSize={onUpdateSize}
                                    onUpdateRotation={onUpdateRotation}
                                    onUpdateSubtype={onUpdateSubtype}
                                    onDelete={onDelete}
                                    onDuplicate={onDuplicate}
                                    collisionResult={collisionResult}
                                    furnitureList={furnitureList}
                                    ignoredCollisions={ignoredCollisions}
                                    onIgnoreCollision={onIgnoreCollision}
                                    onUnignoreCollision={onUnignoreCollision}
                                />
                            )}
                        </group>
                    )
                })}
            </group>
        </>
    )
}
